import React, { FC, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetPOTransactions } from "../../../../../api/services/client/clientApis";
import {
  POTransactionType,
  Transactions,
} from "../../../../../api/services/client/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiLink,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  TableHeaderSearchIcon,
  WarningConfirmationModal,
} from "../../../../../components";
import { AmountWithSign } from "../../../../../components/amountWithSign/AmountWithSign";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  PAGE_URLS,
  SYMBOL,
} from "../../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../../helpers/constants/componentIds";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import {
  checkFilterMatch,
  compareDates,
  convertToUserFormat,
  getCurrencyNameAndLocaleById,
  getFormattedAmount,
  getParsedBEErrors,
  getParsedErrorMessage,
  getUniqueFilters,
  getValueOrBlank,
  getValueOrDash,
  isEmpty,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { currencyListSelector } from "../../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import {
  TransactionsFormKey,
  UniquePOTransactionsResultFiltersTypes,
} from "../../../modelsAndSchemas/Models";
import { purchaseOrdersListSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { pageId } from "../PurchaseOrderManageTransaction";
import { POTransactionsWrapper } from "./PurchaseOrderTransactions.style";

interface POTransactionsProps {
  purchaseOrderId: string;
  currencyTypeId: string;
  refreshTrigger?: boolean;
}

const PURCHASE_ORDER_TRANSACTIONS_TYPE = {
  PROGRAM: "PROGRAM",
  PROJECT: "PROJECT",
} as const;

const PurchaseOrderTransactions: FC<POTransactionsProps> = ({
  purchaseOrderId,
  currencyTypeId,
  refreshTrigger,
}) => {
  const [filteredResults, setFilteredResults] = useState<Transactions[]>([]);
  const [transactions, setTransactions] = useState<Transactions[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const transactionRef = useRef<Transactions>();
  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currencyList = useSelector(currencyListSelector);
  const [uniqueFiltersValues, setUniqueFiltersValues] =
    useState<UniquePOTransactionsResultFiltersTypes>(() => ({
      transactionType: [],
    }));

  const { loggedInUser } = useGetLoggedInUserDetails();
  const { commonLabels, fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    mutateAsync: fetchTransactions,
    data: transactionsData,
    isPending: isTransactionLoading,
    isError: isFetchTransactionFailed,
    error: fetchTransactionError,
  } = useGetPOTransactions();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isPOTransactionPending: {
          isVisible: isTransactionLoading,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isPOTransactionPending: {
            isVisible: false,
            message: "",
          },
        }),
      );
    };
  }, [isTransactionLoading]);

  const { control, getValues } = useForm({
    mode: "all",
    defaultValues: {
      [TransactionsFormKey.transactionDate]: "",
      [TransactionsFormKey.transactionType]: "",
      [TransactionsFormKey.projectOrProgramKey]: "",
    },
  });

  const formValues = useWatch({ control });

  useEffect(() => {
    fetchTransactions(purchaseOrderId);
  }, []);

  useEffect(() => {
    if (refreshTrigger) fetchTransactions(purchaseOrderId);
  }, [refreshTrigger]);

  useEffect(() => {
    if (isNotEmpty(transactionsData)) {
      const localRowsData = [...transactionsData];

      localRowsData.forEach((data) => {
        data.transactionTypeKey = data.transactionType;

        data.transactionTypeValue = getTransactionTypeMappedValue(
          data.transactionType,
        );
      });
      setTransactions(localRowsData);
      setFilteredResults([...localRowsData]);
      setUniqueFiltersValues((prevFilters) => ({
        ...prevFilters,
        [TransactionsFormKey.transactionType]: getUniqueFilters(
          transactionsData,
          "transactionTypeValue",
        ),
      }));
    }
  }, [transactionsData]);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    if (isNotEmpty(transactions)) {
      const { transactionType, projectOrProgramKey, transactionDate } =
        getValues();

      const data = transactions.filter((item) => {
        const isTransactionTypeMatch =
          transactionType === "" ||
          item.transactionTypeValue?.toLowerCase() ===
            transactionType?.toLowerCase();

        const isProjectOrProgramKeyMatch = checkFilterMatch(
          projectOrProgramKey,
          item.projectOrProgramKey,
        );

        let isTransactionDateMatch = compareDates(
          getValueOrBlank(transactionDate),
          getValueOrBlank(item?.createdTimeStamp),
          userCountryDateFormat ?? "",
        );

        return (
          isTransactionTypeMatch &&
          isProjectOrProgramKeyMatch &&
          isTransactionDateMatch
        );
      });

      setFilteredResults([...data]);
      resetPagination();
    }
  }, [formValues]);

  useEffect(() => {
    if (isFetchTransactionFailed && fetchTransactionError) {
      const error = getParsedBEErrors(fetchTransactionError);
      if (isNotEmpty(error)) {
        dispatch(
          toggleToast({
            open: true,
            type: "error",
            message: getParsedErrorMessage(fetchTransactionError),
          }),
        );
      }
    }
  }, [isFetchTransactionFailed, fetchTransactionError]);

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const formatCurrency = (amount: number): string => {
    const currencyDetails = getCurrencyNameAndLocaleById(
      currencyList,
      currencyTypeId,
    );
    const formattedAmount = `${currencyDetails.currencyName} ${getFormattedAmount(amount, currencyDetails.currencyLocale)}`;
    return formattedAmount;
  };

  const formatTransactionAmount = (
    amount: number,
    operationId: number,
  ): React.ReactNode => {
    const sign =
      operationId === POTransactionType.TopUp ? SYMBOL.PLUS : SYMBOL.MINUS;
    return isNotEmpty(amount) ? (
      <AmountWithSign currency={formatCurrency(amount)} sign={sign} />
    ) : (
      EMPTY_VALUE_DASH
    );
  };

  const getLabel = (key: keyof typeof purchaseOrdersListSiteCoreKeys) => {
    return fetchLabel(
      purchaseOrdersListSiteCoreKeys.purchaseOrderTitle,
      purchaseOrdersListSiteCoreKeys[key],
    );
  };

  const getTransactionTypeMappedValue = (transactionTypeKey: string) => {
    return fetchValueByKeyOfDropdown(
      purchaseOrdersListSiteCoreKeys.purchaseOrderTitle,
      purchaseOrdersListSiteCoreKeys.transactionTypeList,
      transactionTypeKey,
    );
  };

  const getProgramOrProject = (
    transaction: Transactions,
    field: "id" | "key",
  ) => {
    if (
      isNotEmpty(transaction?.program) &&
      isNotEmpty(transaction.program[field])
    ) {
      return {
        value: transaction.program[field],
        type: PURCHASE_ORDER_TRANSACTIONS_TYPE.PROGRAM,
      };
    }

    if (
      isNotEmpty(transaction?.project) &&
      isNotEmpty(transaction.project[field])
    ) {
      return {
        value: transaction.project[field],
        type: PURCHASE_ORDER_TRANSACTIONS_TYPE.PROJECT,
      };
    }

    return { value: "", type: null };
  };

  const onProgramProjectClick = (transaction: Transactions) => {
    transactionRef.current = transaction;
    setShowConfirmationModal(true);
  };

  const handleModalClose = () => {
    transactionRef.current = undefined;
    setShowConfirmationModal(false);
  };

  const handleNavigation = (transaction: Transactions) => {
    const { value: id, type } = getProgramOrProject(transaction, "id");

    if (isEmpty(type)) return;
    const route =
      type === PURCHASE_ORDER_TRANSACTIONS_TYPE.PROGRAM
        ? PAGE_URLS.VIEW_PROGRAM
        : PAGE_URLS.VIEW_PROJECT;
    navigate(`${route}/${id}`);
  };

  const handlePurchaseOrderNavigation = () => {
    if (transactionRef.current) {
      handleNavigation(transactionRef.current);
    }
    setShowConfirmationModal(false);
  };

  const getProgramProjectKey = (transaction: Transactions): string => {
    const { value: key } = getProgramOrProject(transaction, "key");
    return key;
  };

  const tableHeaderForm = [
    <MuiDateFieldFormController
      control={control}
      name={TransactionsFormKey.transactionDate}
      key={TransactionsFormKey.transactionDate}
      label={""}
      valueFormat={userCountryDateFormat}
      clearable
      format={userCountryDateFormat}
      id={`${pageId}-${TransactionsFormKey.transactionDate}`}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      hideCalendarIconOnValue={isNotEmpty(
        getValues(TransactionsFormKey.transactionDate),
      )}
    />,
    <></>,
    <FormController
      control={control}
      name={TransactionsFormKey.transactionType}
      key={TransactionsFormKey.transactionType}
    >
      <MuiSelect
        label=""
        options={uniqueFiltersValues.transactionType}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${TransactionsFormKey.transactionType}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
    <></>,
    <></>,
    <FormController
      control={control}
      name={TransactionsFormKey.projectOrProgramKey}
      key={TransactionsFormKey.projectOrProgramKey}
    >
      <MuiTextField
        id={`${pageId}-${TransactionsFormKey.projectOrProgramKey}`}
        startAdornment={<TableHeaderSearchIcon />}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
  ];

  const columns = [
    {
      id: TransactionsFormKey.transactionDate,
      label: getLabel("transactionDate"),
      isSortVisible: true,
      className: "cell-solution-category",
      render: (row: any) => (
        <MuiTypography variant="h3">
          {row.createdTimeStamp
            ? convertToUserFormat(row.createdTimeStamp, userCountryDateFormat)
            : EMPTY_VALUE_DASH}
        </MuiTypography>
      ),
    },
    {
      id: TransactionsFormKey.startBalanceAmount,
      label: getLabel("startingPoBalance"),
      isSortVisible: true,
      className: "cell-line-of-business",
      render: (row: any) => (
        <MuiTypography>{formatCurrency(row.startBalanceAmount)}</MuiTypography>
      ),
    },
    {
      id: TransactionsFormKey.transactionType,
      label: getLabel("transactionType"),
      isSortVisible: true,
      className: "cell-line-of-business",
      render: (row: any) => (
        <MuiTypography>
          {isNotEmpty(row.transactionTypeValue)
            ? row.transactionTypeValue
            : EMPTY_VALUE_DASH}
        </MuiTypography>
      ),
    },
    {
      id: TransactionsFormKey.amount,
      label: getLabel("amount"),
      isSortVisible: true,
      className: "cell-line-of-business",
      render: (row: any) => (
        <MuiBox>
          {formatTransactionAmount(row.adjustmentAmount, row.operationId)}
        </MuiBox>
      ),
    },
    {
      id: TransactionsFormKey.totalBalanceAmount,
      label: getLabel("totalPoBalance"),
      isSortVisible: true,
      className: "cell-program-class",
      render: (row: any) => (
        <MuiTypography>{formatCurrency(row.totalBalanceAmount)}</MuiTypography>
      ),
    },
    {
      id: TransactionsFormKey.invoiceNumber,
      label: "Invoice Number",
      isSortVisible: true,
      className: "cell-line-of-business",
      render: (row: any) => (
        <MuiTypography variant="h3" className="cell-line-of-business">
          {getValueOrDash(row.invoiceNumber)}
        </MuiTypography>
      ),
    },
    {
      id: TransactionsFormKey.projectOrProgramKey,
      label: getLabel("projectOrProgramKey"),
      isSortVisible: true,
      className: "cell-solution-category",
      render: (row: any) => {
        const programProjectKey = getProgramProjectKey(
          row as unknown as Transactions,
        );
        if (isEmpty(programProjectKey)) {
          return <MuiTypography variant="h3">{EMPTY_VALUE_DASH}</MuiTypography>;
        }
        return (
          <MuiLink
            data-testid={`${COMPONENT_IDS.COMMON_VIEW_LINK}-${row.operationId}`}
            onClick={() =>
              onProgramProjectClick(row as unknown as Transactions)
            }
          >
            <MuiEllipseLabel
              labelTypoClassName="Link ellipsis-with-tooltip"
              text={programProjectKey}
              labelTypoVariant="h3"
              placement="bottom-start"
            />
          </MuiLink>
        );
      },
    },
  ];

  return (
    <POTransactionsWrapper>
      <MuiBox className="transaction-table">
        <AccordionHeader
          title={renderHeader(getLabel("poTransactionHistoryHeading"))}
          showExpandCollapseIcon={false}
        >
          <MuiTable
            ref={tableRef}
            pageId={pageId}
            tableHeaderForm={tableHeaderForm}
            columns={columns}
            tableResults={[...filteredResults]}
            noDataInApi={isEmpty(transactions)}
            noResultMessage={commonLabels.noResult}
            emptyMessage={
              isTransactionLoading
                ? commonLabels.loadingPleaseWait
                : commonLabels.noResult
            }
            priorityResults={[]}
          />
        </AccordionHeader>
      </MuiBox>
      {showConfirmationModal && (
        <WarningConfirmationModal
          show={showConfirmationModal}
          onClose={handleModalClose}
          onPrimaryButtonClick={handlePurchaseOrderNavigation}
          modalTitle={commonLabels.confirmation}
          labelContent={getLabel("navigationConfirmationMessage")}
        />
      )}
    </POTransactionsWrapper>
  );
};

export default PurchaseOrderTransactions;
